import React, { useEffect, useState } from 'react';
import { SHARED_UI_LABELS } from '../config/task-display-config';
import { motion, AnimatePresence } from 'framer-motion';
import Inbox from './Inbox';
import StrategyCorner from './StrategyCorner';
import { useLayoutMode } from '../components/layout/LayoutModeContext';

/**
 * CEO Office — Tabbed hub for daily decisions + weekly strategy.
 * V7: Text-only tabs (no icons), 3px active underline, DM Sans heading.
 *
 * Tab 1: Inbox (daily use — campaign/article decisions)
 * Tab 2: Strategy Insights (weekly recommendations)
 */

const TABS = [
    { id: 'inbox', label: 'Inbox' },
    { id: 'strategy', label: SHARED_UI_LABELS.strategyInsightsLabel },
];

export default function CeoOffice() {
    const [activeTab, setActiveTab] = useState('inbox');
    const { setFullBleed } = useLayoutMode();

    // Inbox tab renders the three-pane cockpit and needs full viewport width.
    // Other tabs keep the default centered 840px wrapper.
    useEffect(() => {
        setFullBleed(activeTab === 'inbox');
        return () => setFullBleed(false);
    }, [activeTab, setFullBleed]);

    const isFullBleed = activeTab === 'inbox';
    // When fullBleed, the header + tab bar need their own centered container so
    // they don't flush the viewport edges; the cockpit tab panel itself spans full width.
    const headerWrapperClass = isFullBleed
        ? 'max-w-[1200px] mx-auto px-10 pt-8'
        : '';

    return (
        <>
            <div className={headerWrapperClass}>
            {/* Page Header — V7: plain text, DM Sans, no icon */}
            <div className="mb-2">
                <h1
                    className="text-[28px] font-extrabold text-brand-900 leading-tight font-heading"
                >
                    CEO Office
                </h1>
            </div>

            {/* Tab Bar — V7: text-only, 3px active underline, no icons */}
            <div
                className="flex border-b-2 border-slate-200 mb-6"
                role="tablist"
                aria-label={SHARED_UI_LABELS.ceoOfficeSectionsLabel}
            >
                {TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`tabpanel-${tab.id}`}
                            id={`tab-${tab.id}`}
                            onClick={() => setActiveTab(tab.id)}
                            className={[
                                'relative px-6 min-h-[44px] text-[15px]',
                                'transition-colors duration-150 ease-out cursor-pointer',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2',
                                isActive
                                    ? 'font-semibold text-brand-900 -mb-[2px] border-b-[3px] border-brand-900'
                                    : 'font-medium text-slate-400',
                            ].join(' ')}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>
            </div>

            {/* Tab Panels — crossfade transition */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    id={`tabpanel-${activeTab}`}
                    role="tabpanel"
                    aria-labelledby={`tab-${activeTab}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15, ease: 'easeInOut' }}
                >
                    {activeTab === 'inbox' && <Inbox embedded />}
                    {activeTab === 'strategy' && <StrategyCorner embedded />}
                </motion.div>
            </AnimatePresence>
        </>
    );
}
